import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiSuccess,
  CreateReferenceInput,
  ReferenceRow,
  UpdateReferenceInput,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

export type SettingsPath =
  | 'industries'
  | 'payment-certs'
  | 'payment-methods'
  | 'interview-types'
  // REQ-20260519 ERP Phase 1A
  | 'product-categories'
  | 'product-units'
  | 'supplier-categories'
  | 'warehouses'
  // REQ-20260810001 藝品印刷訂單四個現場欄位的常用選項
  | 'processing-options'
  | 'print-color-options'
  | 'artwork-handling-options'
  | 'shipping-method-options'
  // REQ-20260810003 印刷面別
  | 'print-side-options'
  // REQ-20260814002 進貨類別
  | 'purchase-categories';

/**
 * reference 的 code → name 對照。給「欄位存的是 code、畫面要顯示中文」的地方用
 * (進貨類別是第一個;存 name 的欄位用 `optionNames()`)。
 * 對照不到的 code 交給呼叫端 `?? code` 退回顯示原始值 —— 設定頁刪不掉資料
 * (只會停用),但單據上可能留著更早以前、清單裡已經沒有的 code。
 */
export function referenceNameMap(rows: ReferenceRow[] | undefined): Record<string, string> {
  return Object.fromEntries((rows ?? []).map((r) => [r.code, r.name]));
}

export function useReferences(path: SettingsPath, all = false) {
  return useQuery({
    queryKey: ['settings', path, all],
    queryFn: async (): Promise<ApiSuccess<ReferenceRow[]>> => {
      const res = await apiClient.get<ApiSuccess<ReferenceRow[]>>(`/${path}`, {
        params: all ? { all: 1 } : {},
      });
      return res.data;
    },
  });
}

export function useCreateReference(path: SettingsPath) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateReferenceInput): Promise<ReferenceRow> => {
      const res = await apiClient.post<ApiSuccess<ReferenceRow>>(`/${path}`, input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', path] });
    },
  });
}

export function useUpdateReference(path: SettingsPath) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: number;
      input: UpdateReferenceInput;
    }): Promise<ReferenceRow> => {
      const res = await apiClient.patch<ApiSuccess<ReferenceRow>>(`/${path}/${id}`, input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', path] });
    },
  });
}

export function useDeleteReference(path: SettingsPath) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/${path}/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', path] });
    },
  });
}
